import React, { useCallback, useEffect, useState } from "react";

import { Button } from "../ui/button";
import { useAuth } from "../../contexts/AuthContext";
import { fmtAgo } from "../../lib/format";
import {
  type ReaderJob,
  type ReaderStatus,
  describeJob,
  fetchReaderJobs,
  fetchReaderStatus,
  readersOf,
} from "../../services/readerApi";
import { useReaderJob } from "../../hooks/useReaderJob";
import { useSelectedReader } from "../../hooks/useSelectedReader";
import ReaderAdvanced from "./ReaderAdvanced";
import ReaderPicker, { DisabledReaderNote } from "./ReaderPicker";

/**
 * Reader tools for the ticket-activity modal — the full set, for someone
 * actually working the box.
 *
 * Beyond the map card's test scan this adds **Force Scan**: it restarts the
 * box's own scanner so the BOX runs a real scan with production logic, writes
 * its own scan history, and publishes to the real MQTT topics. That takes
 * 5-10 minutes, so it reports live progress (which round, how many tags so
 * far) and finishes with an explicit completed / zero-tags / failed verdict.
 *
 * Force scan also doubles as the recovery action for a wedged scanner: the
 * restart issues a reader soft-reset on the way up, and a scanner that has
 * silently stopped scanning starts again.
 *
 * Every run is recorded against the box, so the history below is the record of
 * what has been tried on this ticket.
 *
 * **A box can carry two or three readers**, so the test scan and the Advanced
 * controls target the one picked here. Force scan does NOT take a reader: it
 * restarts the box's whole scanner, which walks every reader the box has.
 */
interface ReaderToolsPanelProps {
  scuName: string;
}

export const ReaderToolsPanel: React.FC<ReaderToolsPanelProps> = ({ scuName }) => {
  const { token, can } = useAuth();
  const canOperate = can("operator.triage");
  const [status, setStatus] = useState<ReaderStatus | null>(null);
  const [history, setHistory] = useState<ReaderJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [confirmForce, setConfirmForce] = useState(false);

  const load = useCallback((): void => {
    if (!token) return;
    setLoading(true);
    Promise.all([
      fetchReaderStatus(token, scuName),
      fetchReaderJobs(token, scuName, 8).catch(() => ({ jobs: [] as ReaderJob[] })),
    ])
      .then(([s, h]) => {
        setStatus(s);
        setHistory(h.jobs);
        setLoadError(null);
      })
      .catch((e) => setLoadError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [token, scuName]);

  useEffect(() => {
    load();
  }, [load]);

  const { job, busy, error, runTestScan, runForceScan } = useReaderJob(
    token,
    scuName,
    status?.latest_job ?? null,
  );

  const jobDone = job && !job.is_running;
  useEffect(() => {
    if (jobDone) load();
  }, [jobDone, job?.job_id]);

  const readers = readersOf(status);
  const { selectedId, selected, select } = useSelectedReader(readers);

  if (!canOperate) return null;

  const running = job?.is_running ?? false;
  const disabled = busy || running;

  return (
    <div className="border border-secondary-200 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-secondary-900">RFID reader</h4>
        <button type="button" onClick={load} className="text-xs text-primary-600 hover:underline">
          refresh
        </button>
      </div>

      {loading && !status && <div className="text-sm text-secondary-500">checking…</div>}
      {loadError && (
        <div className="text-xs text-error-700 bg-error-50 border border-error-200 px-3 py-2 rounded mb-3">
          {loadError}
        </div>
      )}

      {status && !status.configured && (
        <div className="text-sm text-secondary-600 mb-3">
          No reader configured on this box.
          {status.error && <div className="text-xs text-secondary-500 mt-1">{status.error}</div>}
        </div>
      )}

      {status?.configured && (
        <>
          <ReaderPicker
            readers={readers}
            selected={selectedId}
            onSelect={select}
            disabled={disabled}
          />
          {selected && !selected.is_active && <DisabledReaderNote reader={selected} />}
          <div className="text-sm text-secondary-700 mb-3">
            {readers.length > 1 ? `Reader ${selected?.reader_id} · ` : ""}
            {selected?.ip_address}:{selected?.port} ·{" "}
            {!selected?.is_active
              ? "switched off at the box"
              : selected?.reader_responding
                ? `reader responding${selected.firmware ? ` (fw ${selected.firmware})` : ""}`
                : selected?.bridge_reachable
                  ? "bridge up, reader SILENT"
                  : "unreachable"}{" "}
            · {selected?.antenna_count} antennas
            {selected?.power_summary ? ` · ${selected.power_summary}` : ""}
            {selected?.temperature_c != null ? ` · ${selected.temperature_c}°C` : ""}
          </div>
        </>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-2">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => void runTestScan(undefined, selectedId ?? undefined)}
          disabled={disabled}
        >
          Test scan
        </Button>
        {!confirmForce ? (
          <Button size="sm" onClick={() => setConfirmForce(true)} disabled={disabled}>
            Force scan
          </Button>
        ) : (
          <span className="inline-flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => {
                setConfirmForce(false);
                void runForceScan();
              }}
              disabled={disabled}
            >
              Yes, restart the scanner
            </Button>
            <button
              type="button"
              className="text-xs text-secondary-500 hover:underline"
              onClick={() => setConfirmForce(false)}
            >
              cancel
            </button>
          </span>
        )}
      </div>

      <p className="text-xs text-secondary-500 mb-3">
        <strong>Test scan</strong> pauses the box&apos;s scanner, reads the reader directly, then
        restarts it — nothing is published, and the tag count appears as soon as the read
        finishes.{" "}
        <strong>Force scan</strong> restarts the box&apos;s scanner so it runs a real scan and
        publishes it — 5-10 minutes, and it interrupts any scan in progress.{" "}
        Both rest ~3 minutes before letting the box scan again: an interrupted scan leaves tags
        flagged in the reader&apos;s S2 session, and a flagged tag stays silent, so scanning too
        soon would under-count and publish that as truth.
      </p>

      {error && <div className="text-xs text-error-700 mb-2">{error}</div>}

      {job && <ActiveJob job={job} />}

      {status?.configured && (
        <ReaderAdvanced
          scuName={scuName}
          status={status}
          disabled={disabled}
          onDone={() => load()}
        />
      )}

      {history.length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-medium text-secondary-500 mb-1">Recent reader actions</div>
          <ul className="text-xs space-y-1">
            {history.slice(0, 6).map((h) => (
              <li key={h.job_id} className="flex gap-2 text-secondary-600">
                <span className="text-secondary-400 w-16 shrink-0">
                  {h.created_utc ? fmtAgo(h.created_utc) : "—"}
                </span>
                <span className="w-20 shrink-0">
                  {h.kind === "force_scan" ? "force scan" : "test scan"}
                </span>
                <span className={h.status === "completed" ? "" : "text-error-700"}>
                  {describeJob(h)}
                </span>
                {h.requested_by && (
                  <span className="text-secondary-400">· {h.requested_by}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

/** The in-flight / just-finished job, with progress while it runs and an
 *  unambiguous verdict when it stops. */
const ActiveJob: React.FC<{ job: ReaderJob }> = ({ job }) => {
  if (job.is_running) {
    const p = job.progress;
    return (
      <div className="text-sm bg-primary-50 border border-primary-200 rounded px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-primary-500 animate-pulse" />
          <span className="font-medium text-primary-900">
            {job.kind === "force_scan" ? "Force scan running" : "Test scan running"}
          </span>
          <span className="text-primary-800">— {job.phase ?? "working"}</span>
        </div>
        {p?.round != null && (
          <div className="text-xs text-primary-800 mt-1">
            round {p.round} · {p.tags ?? 0} tags found
            {p.new_tags != null ? ` · ${p.new_tags} new` : ""}
            {p.zero_rounds ? ` · ${p.zero_rounds} quiet rounds (it ends after the last one)` : ""}
          </div>
        )}
        {p?.elapsed_seconds != null && (
          <div className="text-xs text-primary-700 mt-1">
            {p.elapsed_seconds}s elapsed · a full scan usually takes 5-10 minutes
          </div>
        )}
      </div>
    );
  }

  // Terminal. Three distinct verdicts, never collapsed into one.
  if (job.status === "completed" && !job.read_nothing) {
    const r = job.result;
    return (
      <div className="text-sm bg-success-50 border border-success-200 rounded px-3 py-2">
        <div className="font-medium text-success-900">
          Scan complete — {job.tags_found} tag{job.tags_found === 1 ? "" : "s"} read
        </div>
        <div className="text-xs text-success-800 mt-1">
          {r?.scan?.antennas_used != null && `${r.scan.antennas_used} antennas · `}
          {r?.scan?.duration_seconds != null && `${r.scan.duration_seconds}s · `}
          {r?.rounds != null && `${r.rounds} rounds · `}
          {job.box_scan_id != null && `box scan #${job.box_scan_id}`}
          {r?.seconds != null && !r.scan && `${r.seconds}s`}
        </div>
        {r?.published && r.published.length > 0 && (
          <div className="text-xs text-success-800 mt-1">
            published to {r.published.map((p) => p.kind).join(" + ")}
          </div>
        )}
      </div>
    );
  }

  if (job.status === "completed" && job.read_nothing) {
    return (
      <div className="text-sm bg-warning-50 border border-warning-200 rounded px-3 py-2">
        <div className="font-medium text-warning-900">Scan complete — but ZERO tags were read</div>
        <div className="text-xs text-warning-800 mt-1">
          {job.result?.warning ??
            "The reader answered and finished its rounds, so this is not a reader fault: either nothing is in range, or the antennas are not seeing the stock."}
        </div>
      </div>
    );
  }

  return (
    <div className="text-sm bg-error-50 border border-error-200 rounded px-3 py-2">
      <div className="font-medium text-error-900">
        {job.status === "timed_out" ? "Scan timed out" : "Scan failed"}
      </div>
      {job.error && <div className="text-xs text-error-800 mt-1">{job.error}</div>}
    </div>
  );
};

export default ReaderToolsPanel;
